// src/services/firebaseAuth.js
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  OAuthProvider,
  signOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { apiCall } from './api';

// Google provider setup
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Microsoft provider setup
const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.setCustomParameters({
  prompt: 'consent',
  tenant: process.env.REACT_APP_MICROSOFT_TENANT_ID
});

// Sign in with Google via Firebase using popup
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result;
  } catch (error) {
    console.error('Error with Google sign-in:', error);
    throw error;
  }
};

// Sign in with Microsoft via Firebase using popup
export const signInWithMicrosoft = async () => {
  try {
    const result = await signInWithPopup(auth, microsoftProvider);
    return result;
  } catch (error) {
    console.error('Error with Microsoft sign-in:', error);
    throw error;
  }
};

// Get current user's ID token
export const getIdToken = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('No user is signed in');
  }
  
  return currentUser.getIdToken(true);
};

// Sign out
export const firebaseSignOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Email Link Authentication
export const sendEmailSignInLink = async (email) => {
  try {
    // URL settings for the sign-in link
    const actionCodeSettings = {
      // URL you want to redirect to after sign-in
      url: window.location.href,
      // This must be true for email link sign-in
      handleCodeInApp: true
    };
    
    // Save the email in localStorage to use it later when the user clicks the link
    localStorage.setItem('emailForSignIn', email);
    
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    return true;
  } catch (error) {
    console.error('Error sending sign-in link:', error);
    throw error;
  }
};

// Check and handle sign-in with email link
export const handleEmailLinkSignIn = async (appId = 'saleswit') => {
  if (isSignInWithEmailLink(auth, window.location.href)) {
    let savedEmail = localStorage.getItem('emailForSignIn');
    let savedName = localStorage.getItem('nameForSignIn') || '';
    
    if (!savedEmail) {
      // If email isn't saved, ask the user for it
      savedEmail = window.prompt('Please provide your email for confirmation');
    }
    
    if (savedEmail) {
      try {
        const result = await signInWithEmailLink(auth, savedEmail, window.location.href);
        
        // Clear email and name from storage
        localStorage.removeItem('emailForSignIn');
        localStorage.removeItem('nameForSignIn');
        
        // Remove the email link parameters from URL
        window.history.replaceState(null, null, window.location.pathname);
        
        // Register with backend
        try {
          const idToken = await result.user.getIdToken();
          const response = await apiCall('/api/auth/firebase', {
            method: 'POST',
            body: JSON.stringify({
              token: idToken
            }),
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.token) {
              localStorage.setItem('token', data.token);
            }
          }
        } catch (backendError) {
          console.warn('Warning: Backend registration error', backendError);
        }
        
        return result;
      } catch (error) {
        console.error('Error signing in with email link:', error);
        throw error;
      }
    }
  }
  return null;
};